import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useListener } from '@giteeteam/proxima-sdk-js';
import { publicRuntimeConfig } from 'config/index';
import debug from 'debug';
import { Icons } from 'icons';
import { noop } from 'lodash-es';
import eventBus, { EVENTS_NAME } from 'views/events';

import { classnames } from 'lib/classnames';
import useGlobalConfig from 'lib/hooks/useGlobalConfig';

import {
  MIN_CONTROL_WIDTH,
  MouseOverFromType,
  SIDEBAR_CONTROL,
  SIDEBAR_DEFAULT_STATE as DEFAULT_STATE,
} from './constants';
import { useSidebarState } from './useSidebarState';

import styles from './SidebarContent.module.less';

const cx = classnames(styles);
const log = debug('sidebar:');

interface SidebarProps {
  fixedTop?: boolean;
  children?: React.ReactNode;
}

const CssVariable = {
  SideWidth: '--side-width',
  FlyoutWidth: '--flyout-width',
  ControlWidth: '--control-width',
};

const updateLayoutConfig = (sidebarWidth: number) => {
  eventBus.emit(EVENTS_NAME.changeSidebarWidth, sidebarWidth);
};

const toggleFold = (isFolding?: boolean | undefined) => {
  eventBus.emit(EVENTS_NAME.toggleSideBar, isFolding);
};

const SidebarContent: React.FC<SidebarProps> = ({ children, fixedTop }) => {
  const sidebarRef = useRef(null); // 整个sidebar的dom节点
  const split = useRef(null);
  const dragging = useRef(false);
  const previousClientX = useRef(0);
  const scheduledAnimationFrame = useRef(false);
  const mouseOverFrom = useRef(MouseOverFromType.Other); // 记录鼠标移动的组件方向
  const [isHover, setIsHover] = useState(false); // 是否聚焦到侧边栏
  const [moving, setMoving] = useState(false); // 鼠标是否按下，在移动中
  const { fullScreenItemDrawer } = useGlobalConfig();
  const { isDemo } = publicRuntimeConfig;
  const disabled = useMemo(() => isDemo || fullScreenItemDrawer === 'enabled', [isDemo, fullScreenItemDrawer]);
  const sidebarState = useSidebarState();
  const isFold = useMemo(() => sidebarState.folding, [sidebarState.folding]);
  const sideBarWidth = useMemo(() => sidebarState.sidebarWidth, [sidebarState.sidebarWidth]);

  // 侧边栏展开
  const expandHandler = useCallback(() => {
    const flyoutWidth = sidebarRef.current.style.getPropertyValue(CssVariable.FlyoutWidth);
    log('expand:flyoutWidth', flyoutWidth);
    // 展开时获取flyoutWidth的值还原sideWidth
    sidebarRef.current.style.setProperty(CssVariable.SideWidth, flyoutWidth);
    sidebarRef.current.style.setProperty(CssVariable.ControlWidth, '24px');
  }, []);

  // 侧边栏收起
  const foldHandler = useCallback(() => {
    log('fold:SIDEBAR_CONTROL', MIN_CONTROL_WIDTH);
    // 收起时直接改变sideWidth
    setIsHover(false);
    sidebarRef.current.style.setProperty(CssVariable.SideWidth, `${MIN_CONTROL_WIDTH}px`);
    sidebarRef.current.style.setProperty(CssVariable.ControlWidth, '0');
  }, []);

  useEffect(() => {
    log('init', sideBarWidth);
    const sidebarWidthStr = `${sideBarWidth}px`;
    sidebarRef.current.style.setProperty(CssVariable.SideWidth, sidebarWidthStr);
    sidebarRef.current.style.setProperty(CssVariable.FlyoutWidth, sidebarWidthStr);
  }, [sideBarWidth]);

  useEffect(() => {
    if (isFold) {
      foldHandler();
    } else {
      expandHandler();
    }
  }, [expandHandler, foldHandler, isFold]);

  // 每次鼠标从侧边栏移入收缩图标/收缩图标移入侧边栏，记录移入的方向
  const signMouseFrom = (id: string) => {
    mouseOverFrom.current = id === SIDEBAR_CONTROL ? MouseOverFromType.Control : MouseOverFromType.Sidebar;
    setTimeout(() => {
      mouseOverFrom.current = MouseOverFromType.Other;
    }, 300);
  };

  // 拖拽鼠标时改变左侧栏宽度
  const changeWidth = useCallback((clientX: number) => {
    if (!dragging.current) return;
    // 计算偏移量
    const offsetX = clientX - previousClientX.current;
    previousClientX.current = clientX;

    const sideWidth = sidebarRef.current.style.getPropertyValue(CssVariable.SideWidth);
    if (sideWidth) {
      const halfBodyWidth = Math.ceil(window.document.body.clientWidth / 2); // 一半屏幕宽度
      let sideNumber = pixelToNumber(sideWidth);
      log(
        'width change',
        `clientX:${clientX}, sideNumber:${sideNumber}, offsetX:${offsetX}, nowSide:${sideNumber + offsetX}px`,
      );
      // 这里如果鼠标向右移动超出了拖拽组件的位置，这时再向左拖到时不做处理
      if (clientX > halfBodyWidth + MIN_CONTROL_WIDTH) return;
      // 超出最大或最小都不处理
      if (sideNumber < MIN_CONTROL_WIDTH && offsetX < 0) {
        sideNumber = MIN_CONTROL_WIDTH;
        return;
      }
      if (sideNumber > halfBodyWidth && offsetX > 0) {
        sideNumber = halfBodyWidth;
        return;
      }
      sidebarRef.current.style.setProperty(CssVariable.SideWidth, `${sideNumber + offsetX}px`);
    }
  }, []);

  // 鼠标移动方法
  const splitMoveHandler = useCallback(
    (event: MouseEvent) => {
      if (scheduledAnimationFrame.current) {
        return;
      }

      scheduledAnimationFrame.current = true;
      // requestAnimationFrame
      window.requestAnimationFrame(() => {
        scheduledAnimationFrame.current = false;
        changeWidth(event.clientX);
      });
    },
    [changeWidth],
  );

  // 鼠标抬起
  const onDocumentMouseUp = useCallback(() => {
    dragging.current = false;
    setMoving(false);
    const sideWidth = sidebarRef.current.style.getPropertyValue(CssVariable.SideWidth);
    log('mouse up, sideWidth:', sideWidth);
    const sideNumber = pixelToNumber(sideWidth);
    if (sideNumber < 150) {
      // 如果在侧边栏的150像素范围内，直接收起
      toggleFold(true);
      return;
    } else if (sideNumber < DEFAULT_STATE.sidebarWidth) {
      // 在150到默认宽度的像素范围内，直接展开
      sidebarRef.current.style.setProperty(CssVariable.FlyoutWidth, `${DEFAULT_STATE.sidebarWidth}px`);
      toggleFold(false);
      return;
    }
    // 保存记录宽度
    log('log up', sideNumber);
    updateLayoutConfig(sideNumber);
    sidebarRef.current.style.setProperty(CssVariable.FlyoutWidth, `${sideNumber}px`);
    document.removeEventListener('mousemove', splitMoveHandler);
    document.removeEventListener('mouseup', onDocumentMouseUp);
  }, [splitMoveHandler]);

  // 鼠标按下
  const onDocumentMouseDown = useCallback(
    (event: MouseEvent) => {
      dragging.current = true;
      previousClientX.current = event.clientX;
      setMoving(true);

      document.addEventListener('mousemove', splitMoveHandler);
      document.addEventListener('mouseup', onDocumentMouseUp);
      return () => {
        document.removeEventListener('mousemove', splitMoveHandler);
        document.removeEventListener('mouseup', onDocumentMouseUp);
      };
    },
    [onDocumentMouseUp, splitMoveHandler],
  );

  // 拖拽控件的鼠标事件监听
  useEffect(() => {
    if (split.current) {
      const splitDom = split.current;
      splitDom.addEventListener('mousedown', onDocumentMouseDown);
      return () => {
        if (splitDom) {
          splitDom.removeEventListener('mousedown', onDocumentMouseDown);
        }
      };
    }
  }, [onDocumentMouseDown, split]);

  // 处理鼠标进入侧边栏的展开逻辑
  useEffect(() => {
    if (disabled) return;
    if (sidebarRef.current) {
      let isOver = false;
      const onMouseOver = (event: MouseEvent & { target: { id?: string } }) => {
        if (isOver) return;
        if (mouseOverFrom.current === MouseOverFromType.Other && event.target.id === SIDEBAR_CONTROL) {
          // 这里应该是从其他范围进入控制图标范围
          return;
        }
        // 如果当前是收缩状态，则展开
        if (isFold) {
          setIsHover(true);
          isOver = true;
        }
      };
      const onMouseOut = (event: Event & { target: { id?: string } }) => {
        if (isOver) {
          // 鼠标移出拖拽控件，记录状态
          setIsHover(false);
          isOver = false;
          signMouseFrom(event.target.id);
        }
      };

      const sidebarDom = sidebarRef.current;
      sidebarDom.addEventListener('mouseover', onMouseOver);
      sidebarDom.addEventListener('mouseout', onMouseOut);
      return () => {
        if (sidebarDom) {
          sidebarDom.removeEventListener('mouseover', onMouseOver);
          sidebarDom.removeEventListener('mouseout', onMouseOut);
        }
      };
    }
  }, [sidebarRef, isFold, disabled]);

  useListener('sider-layout-toggle-fold', isDemo ? noop : () => toggleFold());

  return (
    <div id="sidebar" className={cx('sidebar', { fold: isFold, hover: isHover })} ref={sidebarRef}>
      <div className={cx('sidebar-nav')}>
        <div className={cx('sidebar-container', { move: moving })} style={{ top: fixedTop ? 0 : null }}>
          <div className={cx('sidebar-content')}>{children}</div>
          <div className={cx('sidebar-control')} hidden={disabled}>
            <div className={cx('control-shadow')} />
            <div className={cx('control-content')} ref={split}>
              <span />
            </div>
            <button
              className={cx('control-icon', { fold: isFold })}
              data-resize-button="true"
              onClick={() => toggleFold()}
            >
              <span className={cx('icon-content')}>
                <Icons.RightArrowHollow />
              </span>
              <div id={SIDEBAR_CONTROL} className={cx('icon-container')} />
            </button>
          </div>
          {disabled && (
            <div className={cx('sidebar-control')}>
              <div className={cx('control-shadow')} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// 像素转数字
function pixelToNumber(pixel: string, defaults?: number): number {
  if (!pixel) return 0;
  let result = parseInt(pixel);
  if (isNaN(result)) {
    const matches = pixel.match(/(\d+)/g);
    if (matches.length) {
      result = parseInt(matches[0]);
    } else {
      result = defaults || 0;
    }
  }
  return result;
}

export default memo(SidebarContent);
